/**
 * "Use Voice" 跨页面预填数据通道
 *
 * 设计目标：从「我的音色」卡片跳到 `/voice/clone` Upload tab 时，
 * 携带 voiceName / prompt / audioUrl 三个字段，但**不污染地址栏**。
 *
 * 使用 sessionStorage（生命周期同 tab）+ 自定义事件：
 *  - sessionStorage：用于跨页面 navigation 时携带数据
 *  - 自定义事件：用于同一页内（router.push 到当前路径不会重新挂载）触发再次读取
 *
 * 读取一次后会立刻 remove，避免刷新页面意外回填
 */

const STORAGE_KEY = 'voice_clone_use_voice_payload'
const EVENT_NAME = 'voice-clone:use-voice'

export type UseVoiceCloneMode = 'upload' | 'record' | 'manage'

export interface UseVoicePayload {
  mode: UseVoiceCloneMode
  voiceName?: string
  prompt?: string
  audioUrl?: string
}

/** 写入并广播事件；调用方应紧跟着 router.push('/voice/clone') */
export const writeUseVoicePayload = (payload: UseVoicePayload): void => {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    window.dispatchEvent(new CustomEvent(EVENT_NAME))
  } catch {
    // 忽略 sessionStorage 写入异常（隐私模式 / 配额满等）
  }
}

/** 读取并清空。无数据返回 null */
export const consumeUseVoicePayload = (): UseVoicePayload | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    sessionStorage.removeItem(STORAGE_KEY)
    return JSON.parse(raw) as UseVoicePayload
  } catch {
    return null
  }
}

/** 订阅同窗口内的写入事件（同页 router.push 不会重挂载，靠事件触发） */
export const subscribeUseVoicePayload = (handler: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener(EVENT_NAME, handler)
  return () => window.removeEventListener(EVENT_NAME, handler)
}
